import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  APP_ID,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer
} from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import {
  DateAdapter,
  ErrorStateMatcher,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  ShowOnDirtyErrorStateMatcher
} from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter, MomentDateModule } from '@angular/material-moment-adapter';
import { BrowserModule } from '@angular/platform-browser';
import { provideRouter, withEnabledBlockingInitialNavigation, Router } from '@angular/router';
import { AuthClientConfig, AuthGuard, AuthModule } from '@auth0/auth0-angular';
import * as Sentry from '@sentry/angular';
import { ProfileNewService } from 'avn/modules/features/profile-new/profile-new.service';
import { AuthAppService } from 'avn/modules/services/auth-app/auth-app.service';
import { BusinessMainService } from 'avn/modules/services/business-main.service';
import { Constants } from 'avn/modules/services/constants';
import { ErrorInterceptor } from 'avn/modules/services/error-handler';
import { JWTAuthInterceptor } from 'avn/modules/services/interceptors/jwt-auth-interceptor';
import { SsrReplaceService } from 'avn/modules/services/ssr-replace.service';
import { UserService } from 'avn/modules/services/user.service';
import { CanonicalUrlPipe } from 'avn/modules/shared/pipes/canonical-url.pipe';
import { SlugTextPipe } from 'avn/modules/shared/pipes/slug-text.pipe';

import { routes } from './app.routes';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

function configInitializer(): void {
  const ssr = inject(SsrReplaceService);
  const config = inject(AuthClientConfig);
  const authConfig = {
    domain: Constants.AUTH0_CONFIG.domain,
    clientId: Constants.AUTH0_CONFIG.clientId,
    useRefreshTokens: true,
    authorizationParams: {
      redirect_uri: `${ssr.getWindow()?.location.origin ?? ''}/login`
    }
  };
  config.set(authConfig);
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(BrowserModule, MomentDateModule, GoogleMapsModule, MatSnackBarModule, AuthModule.forRoot()),
    // Angular Material still relies on the classic animations module; migrating to the native
    // animate.enter/animate.leave API is a larger, deliberate change.
    provideRouter(routes, withEnabledBlockingInitialNavigation()),
    { provide: APP_ID, useValue: 'serverApp' },
    AuthAppService,
    UserService,
    BusinessMainService,
    CanonicalUrlPipe,
    SlugTextPipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JWTAuthInterceptor,
      multi: true
    },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'auto', appearance: 'outline' } },
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: ErrorHandler, useClass: ErrorInterceptor },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(configInitializer),
    ProfileNewService,
    AuthGuard,
    provideHttpClient(withInterceptorsFromDi())
  ]
};
